import {Button, Dialog} from '@material-ui/core';
import React, {useEffect, useState} from 'react';
import Editor from '@monaco-editor/react';
import {DropArea} from '../../../base-components/StudioDropArea';
import cn from 'classnames';
import FormButton from '../../../base-components/StudioButton/FormButton';
import {StudioPopover} from '../../../base-components/StudioPopover';
import {defaultClassifierPostProcessor} from '../../InferenceApp/defaultCfnPostProcessor';
import {defaultDetectionPostProcessor} from '../../InferenceApp/defaultDtnPostProcessor';
import {FileExplorer} from '../../TestDeployView/FileExplorer';
import './ScriptUploadField.scss';
import {ModelPurpose} from '../../../types/model/ModelPurpose';

type ScriptUploadFieldProps = {
  label: string;
  tooltip: string;
  filePaths: File[];
  className?: string;
  disabled?: boolean;
  onChange: (files: File[]) => void;
  viewOnly?: boolean;
  errorText?: string;
  allowDefault?: boolean;
  useDefault?: ModelPurpose;
  modelPurpose?: ModelPurpose;
};

export const ScriptUploadField = ({
  label,
  tooltip,
  filePaths,
  className,
  disabled,
  allowDefault,
  useDefault,
  modelPurpose,
  onChange,
  viewOnly,
  errorText,
}: ScriptUploadFieldProps) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [fileName] = useState('');
  const [fileContent] = useState('');
  const [useDefaultMutable, setUseDefaultMutable] = useState(useDefault);
  const [defaultPP, setDefaultPP] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (useDefaultMutable === 'Classification') {
      setDefaultPP(defaultClassifierPostProcessor);
    } else if (useDefaultMutable === 'Detection') {
      setDefaultPP(defaultDetectionPostProcessor);
    }
  }, [useDefaultMutable]);

  useEffect(() => {
    if (useDefaultMutable && defaultPP && filePaths.length === 0) {
      onChange([new File([defaultPP], 'defaultPostProcessor.js')]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useDefaultMutable, defaultPP]);

  return (
    <div className={cn('script-upload-field', className)}>
      <label>
        {label} <StudioPopover infoMessage={tooltip} />
      </label>
      <DropArea
        message={`Drag and drop files`}
        onChange={(newFilePaths: File[]) => {
          onChange([...filePaths, ...newFilePaths]);
        }}
        className="script-upload-field__drop-area"
        disabled={disabled}
      />
      <FileExplorer
        files={filePaths}
        onDelete={(item: File, _index: number) => {
          setUseDefaultMutable(undefined);
          onChange(filePaths.filter(file => file.name !== item.name));
        }}
        onClear={() => {
          setUseDefaultMutable(undefined);
          onChange([]);
        }}
        viewOnly={viewOnly}
        icon="delete"
        size="small"
      />
      {allowDefault && !useDefaultMutable && (
        <Button
          color="primary"
          onClick={() => {
            if (modelPurpose && filePaths.length === 0) {
              if (modelPurpose === 'Classification') {
                onChange([
                  new File([defaultClassifierPostProcessor], 'defaultPostProcessor.js'),
                ]);
              } else if (modelPurpose === 'Detection') {
                onChange([
                  new File([defaultDetectionPostProcessor], 'defaultPostProcessor.js'),
                ]);
              }
            }
          }}
          disabled={disabled}
        >
          Use Default
        </Button>
      )}
      <ScriptEditorDialog
        isOpen={isDialogOpen}
        fileName={fileName}
        fileContent={fileContent || ''}
        onClose={() => setIsDialogOpen(false)}
      />
      <div className="script-upload-field__error">{errorText}</div>
    </div>
  );
};

type ScriptEditorDialogProps = {
  fileName: string;
  fileContent: string;
  isOpen: boolean;
  onClose: () => void;
};

const ScriptEditorDialog = ({
  fileName,
  fileContent,
  isOpen,
  onClose,
}: ScriptEditorDialogProps) => {
  return (
    <Dialog open={isOpen} onClose={onClose} className="script-editor-dialog">
      <h2 className="script-editor-dialog__title">{fileName}</h2>
      <div className="script-editor-dialog__body">
        <Editor
          height="100%"
          width="100%"
          path={fileName}
          value={fileContent}
          options={{
            readOnly: true,
          }}
        />
      </div>
      <div className="script-editor-dialog__actions">
        <FormButton buttonRole="primary" value="Close" onClick={onClose} />
      </div>
    </Dialog>
  );
};
